import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import { isDirty } from 'redux-form';
import * as actItem from 'actions/library/item';
import { askConfirmAction } from 'actions/confirm-action';
import * as actOutletPage from 'actions/outletPages/list';
import { onClickRow } from 'thunk/library/item';
import Pusher from 'pusher-js';
import {
  PUSHER_APP_KEY,
  INVENTORY_BUSINESS_PUSHER_CHANNEL,
  INVENTORY_CLONE_OUTLET_PUSHER_EVENT,
} from 'api/constants';
import styles from './List.css';

const LockableHeader = props => {
  const { isLocked, children, ...rest } = props;

  if (isLocked) {
    const cellClass = classNames(props.className, styles.lockedCell);
    return (
      <th {...rest} className={cellClass}>
        <i className="glyphicon glyphicon-lock" />
        &nbsp;
        {children}
      </th>
    );
  }

  return <th {...rest}>{children}</th>;
};

const LockedCell = () => {
  return <span className={styles.lockedCell}>Upgrade to Moka POS</span>;
};

class List extends React.Component {
  componentDidMount() {
    const { businessId } = this.props;
    const pusher = new Pusher(PUSHER_APP_KEY, {
      cluster: 'ap1',
      encrypted: true,
    });
    this.channel = pusher.subscribe(
      INVENTORY_BUSINESS_PUSHER_CHANNEL(businessId)
    );
  }

  componentDidUpdate() {
    const { dispatch } = this.props;
    if (this.channel) {
      this.channel.unbind();
    }
    this.channel.bind(INVENTORY_CLONE_OUTLET_PUSHER_EVENT, data => {
      dispatch(actOutletPage.checkCloningOutletOnProgress(data));
    });
  }

  componentWillUnmount() {
    if (this.channel) {
      this.channel.unbind();
    }
  }

  render() {
    const { items, itemDetail, mokapayMode } = this.props;

    return (
      items.meta !== undefined &&
      items.meta.total_count > 0 && (
        <div className={styles.wrapper}>
          <div className="full-height">
            <div className={`${styles.leftcol} no-padding`} id="mk-col-left">
              <table className={styles['table-header']}>
                <thead>
                  <tr>
                    <th className={styles.headerName}>Name</th>
                    <LockableHeader
                      className={styles.headerCategory}
                      isLocked={mokapayMode}
                    >
                      Category
                    </LockableHeader>
                    <th className={styles.headerPrice}>Pricing</th>
                    <LockableHeader
                      className={styles.headerInStock}
                      isLocked={mokapayMode}
                    >
                      In Stock
                    </LockableHeader>
                    <LockableHeader
                      className={styles.headerStockAlert}
                      isLocked={mokapayMode}
                    >
                      Stock Alert
                    </LockableHeader>
                  </tr>
                </thead>
              </table>
              <div
                className={`${
                  styles['table-content-wrapper']
                } item-library-table-content-wrapper`}
              >
                <InfiniteScroll
                  pageStart={1}
                  loadMore={this._onReachBottom}
                  hasMore={this._hasMore()}
                  useWindow={false}
                  threshold={10}
                  initialLoad={false}
                >
                  <table className={styles['table-content']}>
                    <tbody>
                      {items.meta.total_count > 0 &&
                        items.results.map(item => {
                          return (
                            <tr
                              key={item.id}
                              className={`${
                                itemDetail && itemDetail.id === item.id
                                  ? styles.selected
                                  : ''
                              }`}
                              onClick={() => this._onClickRow(item)}
                            >
                              <td className={styles.headerName}>{item.name}</td>
                              <td className={styles.headerCategory}>
                                {mokapayMode ? (
                                  <LockedCell />
                                ) : (
                                  item.category.name
                                )}
                              </td>
                              <td className={styles.headerPrice}>
                                {this._priceFormatter(item)}
                              </td>
                              <td className={styles.headerInStock}>
                                {mokapayMode ? (
                                  <LockedCell />
                                ) : (
                                  this._inStockFormatter(item)
                                )}
                              </td>
                              <td className={styles.headerStockAlert}>
                                {mokapayMode ? (
                                  <LockedCell />
                                ) : (
                                  this._stockAlertFormatter(item, itemDetail)
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </InfiniteScroll>
              </div>
            </div>
          </div>
        </div>
      )
    );
  }

  _onClickRow = item => {
    const { isDirtyItem } = this.props;
    if (isDirtyItem) {
      this.props.dispatch(askConfirmAction(onClickRow(item)));
    } else {
      this.props.dispatch(onClickRow(item));
    }
  };

  _onReachBottom = () => {
    const { dispatch, currentPage, items, filterState } = this.props;
    dispatch(
      actItem.itemsRequest({ page: currentPage + 1, filterState }, items)
    );
  };

  _hasMore = () => {
    const { currentPage, totalPage } = this.props;
    return currentPage < totalPage;
  };

  _priceFormatter = item => {
    const itemVariants = item.item_variants;

    if (item.is_sales_type_price) {
      return 'Sales Type';
    }

    if (itemVariants.length > 1) {
      return `${itemVariants.length} Prices`;
    }

    if (itemVariants.length < 2 && itemVariants.length !== 0) {
      const { price } = itemVariants[0];
      if (itemVariants[0].is_variable_price || price === null) {
        return 'Variable';
      }
      return this._currencyFormatter(price);
    }
  };

  _currencyFormatter = data => {
    if (isNaN(data)) return 'Rp. 0';
    const currency = 'Rp. ';
    let result = '';
    data = data || 0;
    if (data < 0) {
      data *= -1;
      result = `(${currency}${data.toString()})`;
    } else {
      result = currency + data.toString();
    }
    return result.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
  };

  _inStockFormatter = item => {
    const itemVariants = item.item_variants;
    let total = 0;
    let count = 0;

    for (let i = 0; i < itemVariants.length; i++) {
      const itemVariant = itemVariants[i];

      if (itemVariant.track_stock) {
        count += 1;
        if (itemVariant.in_stock) {
          total += parseInt(itemVariant.in_stock);
        }
      }
    }

    if (count > 0) {
      return total;
    }
    return total === 0 ? '' : total;
  };

  _stockAlertFormatter = (item, itemDetail) => {
    const itemVariants = item.item_variants;
    let text = '';
    let color = '#FFD700';
    const whiteColor = '#FFFFFF';
    let lowTotal = 0;
    let outTotal = 0;
    let counter = 0;
    if (itemVariants.length > 0) {
      for (let i = 0; i < itemVariants.length; i++) {
        const itemVariant = itemVariants[i];
        if (itemVariant.track_stock && itemVariant.alert) {
          counter += 1;
          if (
            itemVariant.in_stock <= itemVariant.stock_alert &&
            itemVariant.in_stock > 0
          ) {
            lowTotal += 1;
          } else if (itemVariant.in_stock <= 0) {
            outTotal += 1;
          }
        }
      }

      if (outTotal > 0) {
        color = '#FF0000';
      }
      // $scope.color = color;

      if (outTotal === 0 && lowTotal > 0) {
        text = `${lowTotal} Low`;
      } else if (outTotal > 0 && lowTotal === 0) {
        text = `${outTotal} Out`;
      } else {
        text = `${lowTotal} Low, ${outTotal} Out`;
      }
    }

    if (counter > 0) {
      if (outTotal > 0 || lowTotal > 0) {
        const style = { color };
        const styleWhite = { whiteColor };
        if (itemDetail && itemDetail.id === item.id) {
          return <span style={styleWhite}>{text}</span>;
        }
        return <span style={style}>{text}</span>;
      }
    }

    return '';
  };
}

function mapStateToProps(state) {
  const {
    items,
    itemDetail,
    currentPage,
    totalPage,
    showDetail,
    filterState,
  } = state.library.item;
  return {
    items,
    itemDetail,
    currentPage,
    totalPage,
    showDetail,
    isDirtyItem: isDirty('item')(state),
    filterState,
    businessId: state.moka.user.business.id,
  };
}

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

List.propTypes = {
  dispatch: PropTypes.func,
  items: PropTypes.array,
  itemDetail: PropTypes.object,
  currentPage: PropTypes.number,
  totalPage: PropTypes.number,
  showDetail: PropTypes.bool,
  mokapayMode: PropTypes.bool,
};

List.defaultProps = {
  mokapayMode: false,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(List);
